import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext } from '../../element.model';
import { BaseFeatureCreator } from '../feature.model';
import { DERIVATIVE_DEFAULT_PARAMS, type DerivativeParams } from './derivative.model';

/**
 * Creates derivative curves from parent functions.
 *
 * Uses the parent function's getDerivative() method to compute the derivative.
 * Supports multiple orders (1st, 2nd, etc. derivatives).
 * The curve is reactive and updates when the parent function changes.
 */
export class DerivativeCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'derivative';

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory } = context;

    // Get the parent function from params
    const params = def.params as DerivativeParams | undefined;
    if (!params?.of) {
      throw new Error('Derivative feature requires params.of to specify the parent function');
    }
    const parentFn = this.getParentFunction(params.of, context);
    const order = params.order ?? DERIVATIVE_DEFAULT_PARAMS.order ?? 1;
    const objects: GeometryElement[] = [];

    // Create the derivative evaluation function
    // The curve reads from the parent's getDerivative() which is reactive
    const evaluate = (x: number) => {
      const derivativeFn = parentFn.getDerivative(order);
      return derivativeFn(x);
    };

    // Create Curve using elementFactory
    const curveResult = elementFactory.curve({
      type: 'functiongraph',
      fn: evaluate,
      style: display?.curve,
    });
    objects.push(curveResult.curve);

    return {
      id,
      type: 'derivative',
      objects,
      evaluate,
      // Derivative of a derivative
      getDerivative: (nextOrder = 1) => parentFn.getDerivative(order + nextOrder),
      destroy: () => {
        objects.forEach((obj) => board.removeObject(obj));
      },
    };
  }
}
